<template>
  <section
    id="team-section"
    class="team-module-component">
    <div class="wrapper">
      <div
        class="top-content"
        v-html="data.top_content" />
      <div class="category-selector">
        <div
          v-for="(cat, indx) of data.groups"
          :key="`cat-name-${indx}`"
          :class="{ active: activeTab === indx }"
          class="item"
          @click="activeTab = indx">
          {{ cat.group_name }}
        </div>
      </div>
      <div class="team-list">
        <a
          v-for="(team, index) of data.groups[activeTab].group_selection"
          :key="`team-${activeTab}-${index}`"
          :href="team.url"
          class="team-item">
          <div class="team-image">
            <div class="overlay"></div>
            <img
              :src="team?.featured_image?.full?.url || thumb_placeholder"
              alt="team-image" />
          </div>
          <div class="team-info">
            <h3>{{ team.post_title }}</h3>
            <p v-html="team.acf.title"></p>
          </div>
        </a>
      </div>
    </div>
  </section>
</template>

<script setup>
/* #META
 {
  "NAME": "TeamComponent",
  "CLASSNAME": "team-module-component",
  "URL": "http://lifemine.local/templates/"
 }
#ENDMETA */

import { onMounted, ref, watch } from 'vue'
import thumb_placeholder from '@/assets/img/placeholder-img.jpg'
import gsap from 'gsap'
import { useRouter } from 'vue-router'

const router = useRouter()
const props = defineProps(['data'])
const activeTab = ref(0)

watch(activeTab, () => {
  stagger()
})

function stagger() {
  setTimeout(() => {
    gsap.fromTo(
      '.team-item',
      {
        opacity: 0,
        y: 20,
      },
      {
        opacity: 1,
        y: 0,
        duration: 0.3,
        stagger: 0.1,
      }
    )
  }, 100)
}

const normalizer = (str) => {
  return str.replace(/\s+/g, '-').toLowerCase()
}

const getDefault = () => {
  if (router.currentRoute.value.query?.teamType) {
    const team = props.data.groups.findIndex(
      (group) =>
        normalizer(group.group_name) ===
        normalizer(router.currentRoute.value.query.teamType)
    )
    if (team !== -1) {
      activeTab.value = team
    }
  }
}

onMounted(() => {
  getDefault()
  stagger()
})
</script>

<style lang="scss">
.team-module-component {
  @apply container relative mb-10 px-sm lg:mt-6 xl:px-7;
  .wrapper {
    @apply rounded-2xl bg-cover bg-center bg-no-repeat px-sm py-9 xl:px-[148px] xl:py-[95px];
    background-image: url('@/assets/img/team_gallery.svg');

    .top-content {
      @apply mb-16 px-sm xl:px-0;
      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        @apply header-2 mb-6 xl:mb-8;
        strong {
          @apply font-light text-bicara-blue;
        }
      }
      p {
        @apply body-md;
      }
    }
    .category-selector {
      @apply mx-auto mb-12 flex h-28 w-10/12 flex-col items-center justify-center rounded-3xl border border-black p-1 lg:h-14 lg:w-8/12 lg:flex-row lg:rounded-full;
      .item {
        @apply body-md flex h-full w-full cursor-pointer items-center justify-center rounded-full text-center transition-colors duration-500 ease-in-out;
        &.active {
          @apply bg-bicara-yellow;
        }
      }
    }
    .team-list {
      @apply mt-6 grid grid-cols-2 gap-x-2 lg:grid-cols-3 lg:gap-x-8;
      .team-item {
        @apply mb-0 opacity-0 lg:mb-9;
        .team-image {
          @apply relative mb-2 h-[250px] w-full overflow-hidden md:h-[420px] lg:mb-5;
          .overlay {
            @apply pointer-events-none absolute z-2 h-full w-full bg-bicara-blue/10 opacity-0 transition-opacity duration-500 ease-in-out;
          }
          &:hover {
            .overlay {
              @apply opacity-100;
            }
          }
          img {
            @apply h-full w-full scale-100 rounded-lg object-cover object-top transition-transform duration-500 ease-in-out;
            &:hover {
              @apply scale-[102%] transform;
            }
          }
        }
        .team-info {
          @apply text-center;
          h3 {
            @apply body-sm mb-2 leading-[30px] lg:body-md;
          }
          p {
            @apply caption lg:body-sm lg:mb-0;
          }
        }
      }
    }
  }
}
</style>
